// Press Release List Items: year-filter pills + "Load more" pagination for
// blockPressReleaseListItems (Figma node 550:11818). The first page is rendered server-side
// (BlockPressReleaseListItemsViewComponent) for SEO/crawlability - this only takes over for
// anything beyond that first page, or when the year filter changes the result set entirely.
// The year pills and "Load more" are links (?year=, ?page=) that crawlers and AI agents can
// follow; a plain click is taken over here, one with a modifier key opens the link as usual.
//
// Follows main.ts's data-* init convention (see button-hover.ts/block-map.ts): one class instance
// per [data-press-release-list] root, found via querySelectorAll.

import { countText, formatText } from "../i18n/count-text";
import { bindScrollRow } from "./scroll-row-wheel";

const PAGE_SIZE = 10;

interface PressReleaseItem {
    /** Encoded on the server (EditorText.ToHtml) - only <sup>/<sub>/<br> are markup. */
    headingHtml: string;
    publishDateIso: string;
    publishDateLabel: string;
    url: string | null;
}

interface PressReleasesResponse {
    items: PressReleaseItem[];
    totalCount: number;
    // Where the next page starts - past every index position this one covered, also ones the
    // server dropped (no longer published), so it isn't items.length.
    nextSkip: number;
    hasMore: boolean;
}

// The server's ?year=/&page= link for a list state - BlockPressReleaseListItemsQuery.Href.
export function listHref(path: string, year: number | null, pages: number): string {
    const params = new URLSearchParams();
    if (year !== null) {
        params.set("year", String(year));
    }
    if (pages > 1) {
        params.set("page", String(pages));
    }
    const query = params.toString();
    return query ? `${path}?${query}` : path;
}

// A click the browser should handle itself: a new tab/window (modifier key, middle button).
function opensElsewhere(event: MouseEvent): boolean {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

// The pills and "Load more" are links with role="button": Space activates a button, so it has
// to here too (Enter already clicks a link).
function clickOnSpace(link: HTMLAnchorElement): void {
    link.addEventListener("keydown", (event) => {
        if (event.key === " ") {
            event.preventDefault();
            link.click();
        }
    });
}

class PressReleaseList {
    private readonly root: HTMLElement;
    private readonly rowsContainer: HTMLElement;
    private readonly loadMoreButton: HTMLAnchorElement;
    private readonly countLabel: HTMLElement | null;
    private readonly statusElement: HTMLElement | null;
    private readonly loadingIndicator: HTMLElement | null;
    private readonly yearButtons: HTMLAnchorElement[];
    // The page's culture: /api/press-releases has none of its own, so later rows would otherwise
    // come back in the default language.
    private readonly culture: string | undefined;

    private skip: number;
    // How many pages of rows are showing - "Load more" links to the next one.
    private pages: number;
    private activeYear: number | null = null;
    private loading = false;
    // A Back/Forward that arrived while a fetch was in flight - applied once it's done.
    private urlChangedWhileLoading = false;

    constructor(root: HTMLElement) {
        this.root = root;
        this.rowsContainer = root.querySelector<HTMLElement>("[data-press-release-list-rows]")!;
        this.loadMoreButton = root.querySelector<HTMLAnchorElement>("[data-press-release-list-load-more]")!;
        this.countLabel = root.querySelector<HTMLElement>("[data-press-release-list-count]");
        this.statusElement = root.querySelector<HTMLElement>("[data-press-release-list-status]");
        this.loadingIndicator = root.querySelector<HTMLElement>("[data-press-release-list-loading]");
        this.yearButtons = Array.from(root.querySelectorAll<HTMLAnchorElement>("[data-press-release-list-year]"));

        // The Year row scrolls sideways on mobile widths (block-press-release-list-items.css).
        const yearRow = root.querySelector<HTMLElement>("[data-press-release-list-years]");
        if (yearRow) {
            bindScrollRow(yearRow);
        }

        // The server already rendered the first page - skip starts past it (data-next-skip), not at 0.
        this.skip = Number(root.dataset.nextSkip ?? this.rowsContainer.children.length);
        this.pages = Number(root.dataset.pages ?? 1);
        this.culture = root.dataset.culture || undefined;

        // BlockPressReleaseListItemsViewComponent already resolved ?year= against the real
        // available years (falling back to unfiltered on an unknown/stale value) and rendered
        // the matching pill's data-active/aria-pressed accordingly - read that back rather than
        // re-parsing location.search and re-implementing the same validation here, so the two
        // never disagree about what "active" means for a given URL.
        const activeYearAttr = root.dataset.activeYear;
        this.activeYear = activeYearAttr ? Number(activeYearAttr) : null;

        this.loadMoreButton.addEventListener("click", (event) => {
            if (!opensElsewhere(event)) {
                event.preventDefault();
                void this.loadMore();
            }
        });
        clickOnSpace(this.loadMoreButton);
        this.yearButtons.forEach((button) => {
            button.addEventListener("click", (event) => {
                if (!opensElsewhere(event)) {
                    event.preventDefault();
                    this.toggleYear(button);
                }
            });
            clickOnSpace(button);
        });

        // Back/Forward through a year-filter change (pushState in toggleYear below) doesn't
        // reload the page, so this has to re-sync from the URL itself and refetch - otherwise
        // Back would change the address bar back to an earlier year but leave the rows/active
        // pill exactly as they were, making Back appear to do nothing.
        window.addEventListener("popstate", () => this.applyYearFromUrl());
    }

    private applyYearFromUrl(): void {
        if (this.loading) {
            this.urlChangedWhileLoading = true;
            return;
        }

        const yearParam = new URL(window.location.href).searchParams.get("year");
        const year = yearParam === null ? null : Number(yearParam);
        const validYear = year !== null && this.yearButtons.some((button) => Number(button.dataset.pressReleaseListYear) === year)
            ? year
            : null;

        if (validYear === this.activeYear) {
            return;
        }

        void this.showYear(validYear, false);
    }

    // The pills' state and links: the active one links back to all years, the others to their year.
    private setActivePillState(): void {
        this.yearButtons.forEach((candidate) => {
            const year = Number(candidate.dataset.pressReleaseListYear);
            const isActive = this.activeYear !== null && year === this.activeYear;
            candidate.dataset.active = String(isActive);
            candidate.setAttribute("aria-pressed", String(isActive));
            candidate.href = listHref(window.location.pathname, isActive ? null : year, 1);
        });
    }

    // Keeps the address bar (and so the deep-link/shareable URL) in sync with the active year
    // filter, without a full page reload - pushState so Back/Forward step through filter changes
    // and a shared/bookmarked link lands on the same filtered view the URL implies.
    private syncUrl(): void {
        const url = new URL(window.location.href);
        if (this.activeYear === null) {
            url.searchParams.delete("year");
        } else {
            url.searchParams.set("year", String(this.activeYear));
        }
        window.history.pushState({}, "", url);
    }

    // role="status" (see BlockPressReleaseListItems.cshtml) is a polite live region - assistive
    // tech announces this text whenever it changes, without moving focus. Kept as a short summary
    // rather than reading out every new row's heading, which would be noisy on every "Load more".
    // The messages are dictionary templates rendered as data-text-* on the status element.
    private announce(textKey: string, ...args: (string | number)[]): void {
        if (this.statusElement) {
            this.statusElement.textContent = formatText(this.statusElement.dataset[textKey] ?? "", ...args);
        }
    }

    private announceLoadingYear(): void {
        if (this.activeYear === null) {
            this.announce("textLoadingAll");
        } else {
            this.announce("textLoadingYear", this.activeYear);
        }
    }

    private toggleYear(button: HTMLAnchorElement): void {
        if (this.loading) {
            return;
        }

        const year = Number(button.dataset.pressReleaseListYear);
        void this.showYear(this.activeYear === year ? null : year, true);
    }

    // Swaps the rows for another year's (null: all years). The pill (and, from a pill click, the
    // URL) change right away; if the fetch fails they go back to the year whose rows are still
    // showing - otherwise "Load more" would carry on with the new year's pages under the old
    // year's rows. (After a failed Back/Forward the address bar can't be taken back.)
    private async showYear(year: number | null, pushUrl: boolean): Promise<void> {
        const previousYear = this.activeYear;
        this.activeYear = year;
        this.setActivePillState();
        if (pushUrl) {
            this.syncUrl();
        }
        this.announceLoadingYear();

        const loaded = await this.fetchAndRender(true);
        if (!loaded) {
            this.activeYear = previousYear;
            this.setActivePillState();
            // Takes back our own history entry (its popstate then finds the URL matching again).
            // Not when Back/Forward already moved off it - that's applied below instead.
            if (pushUrl && !this.urlChangedWhileLoading) {
                window.history.back();
            }
        }
        this.applyUrlChangedWhileLoading();
    }

    private async loadMore(): Promise<void> {
        if (this.loading) {
            return;
        }
        this.announce("textLoadingMore");
        await this.fetchAndRender(false);
        this.applyUrlChangedWhileLoading();
    }

    private applyUrlChangedWhileLoading(): void {
        if (this.urlChangedWhileLoading) {
            this.urlChangedWhileLoading = false;
            this.applyYearFromUrl();
        }
    }

    // replacing: a year-filter change fetches a whole new result set (and count) rather than
    // appending - the OLD rows stay on screen (dimmed via [aria-busy] in CSS) until the new ones
    // are ready, then get swapped in one go, instead of clearing to empty space first (see the
    // loading-dots overlay floating in a blank area that prompted this - a filter change should
    // read as a content swap, not a flash of nothing). Nothing changes if the fetch fails.
    // Resolves to whether it loaded.
    private async fetchAndRender(replacing: boolean): Promise<boolean> {
        this.loading = true;
        // aria-disabled (not the disabled attribute) keeps the button focusable and announced
        // while a fetch is in flight, rather than dropping it out of the tab order entirely -
        // click handling below still no-ops via the `loading` guard on toggleYear/loadMore.
        this.loadMoreButton.setAttribute("aria-disabled", "true");
        this.rowsContainer.setAttribute("aria-busy", "true");
        if (this.loadingIndicator) {
            this.loadingIndicator.hidden = false;
        }

        try {
            const params = new URLSearchParams({ skip: String(replacing ? 0 : this.skip), take: String(PAGE_SIZE) });
            if (this.activeYear !== null) {
                params.set("year", String(this.activeYear));
            }
            if (this.culture) {
                params.set("culture", this.culture);
            }

            // Trailing slash: the server 301s any extensionless path without one.
            const response = await fetch(`/api/press-releases/?${params.toString()}`);
            if (!response.ok) {
                throw new Error(`Unexpected status ${response.status}`);
            }
            const payload = (await response.json()) as PressReleasesResponse;

            if (replacing) {
                this.rowsContainer.replaceChildren();
            }
            const firstNewRow = this.rowsContainer.children.length;
            payload.items.forEach((item) => this.rowsContainer.append(this.buildRow(item)));
            this.skip = payload.nextSkip;
            this.pages = replacing ? 1 : this.pages + 1;
            this.loadMoreButton.href = listHref(window.location.pathname, this.activeYear, this.pages + 1);

            // Hiding a focused Load more would drop focus to the top of the page: the next Tab
            // carries on from the first row it just added instead.
            const hadFocus = document.activeElement === this.loadMoreButton;
            this.loadMoreButton.hidden = !payload.hasMore;
            if (hadFocus && this.loadMoreButton.hidden) {
                this.rowsContainer.querySelectorAll<HTMLElement>(":scope > a")[firstNewRow]?.focus();
            }

            const count = payload.totalCount;
            if (replacing && this.countLabel) {
                this.countLabel.textContent = countText(this.countLabel, count);
            }
            this.announce(count === 1 ? "textShowingOne" : "textShowingOther", this.rowsContainer.children.length, count);
            return true;
        } catch (error) {
            console.error("[press-release-list] failed to load press releases", error);
            this.announce("textError");
            return false;
        } finally {
            this.loading = false;
            this.loadMoreButton.removeAttribute("aria-disabled");
            this.rowsContainer.setAttribute("aria-busy", "false");
            if (this.loadingIndicator) {
                this.loadingIndicator.hidden = true;
            }
        }
    }

    private buildRow(item: PressReleaseItem): HTMLAnchorElement {
        const row = document.createElement("a");
        // A hover trigger of its own in button-hover.ts, which drives the fill-slide hover (see
        // block-press-release-list-items.css) via --button-hover-x/y.
        row.className = "block-press-release-list-items__row";
        if (item.url) {
            row.href = item.url;
        }

        const content = document.createElement("span");
        content.className = "block-press-release-list-items__content";

        const time = document.createElement("time");
        time.className = "block-press-release-list-items__date";
        time.dateTime = item.publishDateIso;
        time.textContent = item.publishDateLabel;

        const heading = document.createElement("p");
        heading.className = "block-press-release-list-items__heading";
        heading.innerHTML = item.headingHtml;

        content.append(time, heading);

        const icon = document.createElement("span");
        icon.className = "material-symbols block-press-release-list-items__arrow-icon";
        icon.setAttribute("aria-hidden", "true");
        icon.textContent = "arrow_forward";

        row.append(content, icon);
        return row;
    }
}

export function initPressReleaseList(): void {
    document.querySelectorAll<HTMLElement>("[data-press-release-list]").forEach((root) => {
        new PressReleaseList(root);
    });
}
